// Isolated, replaceable asymmetric module for Nexus E2E (О-ГР-2, О-ГР-3).
//
// Provides ONLY the non-§6 primitives: X25519 (DH), Ed25519 (signatures) and
// HKDF-SHA256 (KDF). These are the "interim" primitives per the requirements:
// they live entirely in this file so they can later be swapped for WebCrypto or
// for primitives mandated by Uzbek regulation WITHOUT touching the AES-256-GCM
// core (ozcrypto) or the X3DH / Double-Ratchet protocol layers.
//
// Backed by @noble/curves + @noble/hashes (audited, pure-JS, browser + node).

import { x25519, ed25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { hmac } from '@noble/hashes/hmac';
import { sha256 } from '@noble/hashes/sha256';
import { randomBytes } from '@noble/hashes/utils';

// --- X25519 (Diffie-Hellman) ---

export function generateDHKeyPair() {
  const priv = x25519.utils.randomPrivateKey();
  return { priv, pub: x25519.getPublicKey(priv) };
}

// Raw X25519 shared secret (32 bytes) between our private and their public key.
export function dh(ourPriv, theirPub) {
  return x25519.getSharedSecret(ourPriv, theirPub);
}

// --- Ed25519 (identity signatures, for signed prekeys — КР-9) ---

export function generateSigningKeyPair() {
  const priv = ed25519.utils.randomPrivateKey();
  return { priv, pub: ed25519.getPublicKey(priv) };
}

export function sign(priv, message) {
  return ed25519.sign(message, priv);
}

export function verify(pub, message, signature) {
  return ed25519.verify(signature, message, pub);
}

// --- HKDF-SHA256 (key derivation) ---

export function kdf(ikm, salt, info, length) {
  const s = salt || new Uint8Array(32);
  const i = typeof info === 'string' ? new TextEncoder().encode(info) : (info || new Uint8Array(0));
  return hkdf(sha256, ikm, s, i, length);
}

// HMAC-SHA256 — used for the Double-Ratchet symmetric chain KDF.
export function mac(key, data) {
  return hmac(sha256, key, data);
}

// --- CSPRNG (КР-2) ---

export function random(n) {
  return randomBytes(n);
}
